import * as Dialog from '@cannect/new-components/atoms/Dialog'
import { cn } from '@cannect/lib/utils'
import { Check, ChevronLeft, CircleHelp, Loader2, LogOut, X } from 'lucide-react'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useHistory } from 'react-router-dom'
import { TRAIL_STEP_TOTAL } from './paths'
import logo from '@cannect/assets/svg/logo2.svg'
import { CLINICAL_PROGRESS_SPAN, CLINICAL_PROGRESS_START, SUPPORT_WHATSAPP_URL } from './copy'

interface TrailShellProps {
  open: boolean
  onClose: () => void
  stepIndex: number
  // Passo usado só pelo stepper do desktop; quando ausente, usa stepIndex.
  stageStepIndex?: number
  onBack?: () => void
  children: ReactNode
  footer?: ReactNode
  hideProgress?: boolean
  showHelp?: boolean
  surface?: boolean
  hideHeader?: boolean
  // Telas em 2 colunas (carrinho, resultado do IMC): alarga a área de conteúdo no desktop.
  wide?: boolean
  // false fecha direto, sem o aviso de saída (ex.: pessoa que não pode seguir no programa).
  confirmClose?: boolean
  // Muda a cada passo/tela; a área rolável volta ao topo quando ela muda.
  scrollKey?: string
  // Porcentagem fixa da barra; substitui o cálculo por passo.
  progressOverride?: number
  headerTitle?: { title: string; subtitle: string }
  // A própria tela mostra os botões do rodapé no desktop (ex.: card de preço da oferta); o rodapé fica só com o voltar.
  actionsInBody?: boolean
  // Trilha encerrada (respostas enviadas): o stepper do desktop marca todas as etapas.
  completed?: boolean
}

// Etapas do wizard no desktop; `until` é o último índice de passo (ver stepIndexFor) que pertence à etapa.
const WIZARD_STAGES = [
  { label: 'Seus dados', until: 3 },
  { label: 'Avaliação', until: 7 },
  { label: 'Seu plano', until: 8 },
  { label: 'Pagamento', until: 14 },
  { label: 'Consulta', until: 17 },
  { label: 'Perguntas médicas', until: TRAIL_STEP_TOTAL }
] as const

const iconButtonClass =
  'inline-flex size-10 shrink-0 items-center justify-center rounded-xl border border-solid border-[#E4E4E4] bg-white text-[#1A1A1A]'

export const TrailCta = ({
  children,
  disabled,
  loading,
  onClick,
  type = 'button'
}: {
  children: ReactNode
  disabled?: boolean
  // Chamada à API em andamento: bloqueia novos cliques e mostra o spinner no lugar do texto.
  loading?: boolean
  onClick?: () => void
  type?: 'button' | 'submit'
}) => (
  <button
    type={type}
    disabled={disabled || loading}
    aria-busy={loading || undefined}
    onClick={onClick}
    className={cn(
      'inline-flex h-12 w-full items-center justify-center gap-2 rounded-full border-0 bg-marketplace-800 px-6 text-sm font-semibold text-white disabled:opacity-50',
      loading && 'cursor-wait'
    )}>
    {loading ? <Loader2 className="size-5 animate-spin" aria-hidden /> : children}
  </button>
)

export const TrailGhost = ({
  children,
  disabled,
  onClick,
  href
}: {
  children: ReactNode
  disabled?: boolean
  onClick?: () => void
  href?: string
}) => {
  const className =
    'mt-3 inline-flex w-full items-center justify-center bg-transparent text-sm font-medium text-[#1A1A1A]'

  if (href) {
    return (
      <a href={href} target="_blank" rel="noreferrer" className={className}>
        {children}
      </a>
    )
  }

  return (
    <button type="button" disabled={disabled} onClick={onClick} className={cn(className, 'border-0')}>
      {children}
    </button>
  )
}

const ProgressBar = ({ progress, className }: { progress: number; className?: string }) => (
  <div className={cn('h-1.5 flex-1 overflow-hidden rounded-full bg-[#E9EDDD]', className)}>
    <div
      className="h-full rounded-full bg-marketplace-800 transition-[width] duration-500"
      style={{ width: `${progress}%` }}
    />
  </div>
)

const STAGE_STATUS = { done: 'Concluída', active: 'Etapa atual', next: '' } as const

const WizardStepper = ({
  stepIndex,
  progress,
  completed
}: {
  stepIndex: number
  progress?: number
  completed?: boolean
}) => {
  const current = WIZARD_STAGES.findIndex((stage) => stepIndex <= stage.until)
  let active = current === -1 ? WIZARD_STAGES.length - 1 : current
  // Índice além da última etapa: todas aparecem como concluídas.
  if (completed) active = WIZARD_STAGES.length
  const last = WIZARD_STAGES.length - 1

  // Quanto da etapa atual já foi feito; nas perguntas médicas segue a barra por pergunta (progressOverride).
  const activeFill = () => {
    if (completed) return 1
    if (active === last && progress !== undefined) {
      return (progress - CLINICAL_PROGRESS_START) / CLINICAL_PROGRESS_SPAN
    }
    const start = active > 0 ? WIZARD_STAGES[active - 1].until : 0
    return (stepIndex - start) / (WIZARD_STAGES[active].until - start)
  }
  const fill = Math.round(Math.min(1, Math.max(0.12, activeFill())) * 100)

  return (
    <ol className="m-0 grid w-full list-none grid-cols-6 gap-3 p-0">
      {WIZARD_STAGES.map((stage, index) => {
        let status: keyof typeof STAGE_STATUS = 'next'
        if (index < active) status = 'done'
        if (index === active) status = 'active'
        const width = { done: 100, active: fill, next: 0 }[status]
        return (
          <li
            key={stage.label}
            aria-current={status === 'active' ? 'step' : undefined}
            className="flex min-w-0 flex-col gap-2">
            <div className="flex min-w-0 items-center gap-2">
              <span
                className={cn(
                  'inline-flex size-6 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold',
                  status === 'done' && 'bg-marketplace-800 text-white',
                  status === 'active' && 'bg-white text-marketplace-900 ring-2 ring-marketplace-800',
                  status === 'next' && 'border border-solid border-[#DADADA] bg-white text-[#9A9A9A]'
                )}>
                {status === 'done' ? <Check className="size-3.5" strokeWidth={3} /> : index + 1}
              </span>
              <span className="flex min-w-0 flex-col leading-tight">
                <span
                  title={stage.label}
                  className={cn(
                    'truncate text-[13px]',
                    status === 'done' && 'font-medium text-[#433F3E]',
                    status === 'active' && 'font-semibold text-[#1A1A1A]',
                    status === 'next' && 'text-[#9A9A9A]'
                  )}>
                  {stage.label}
                </span>
                <span
                  className={cn(
                    'h-[14px] truncate text-[11px]',
                    status === 'done' ? 'text-marketplace-800' : 'text-[#6B6560]'
                  )}>
                  {STAGE_STATUS[status]}
                </span>
              </span>
            </div>
            <span className="block h-1.5 overflow-hidden rounded-full bg-[#E9EDDD]">
              <span
                className="block h-full rounded-full bg-marketplace-800 transition-[width] duration-500"
                style={{ width: `${width}%` }}
              />
            </span>
          </li>
        )
      })}
    </ol>
  )
}

const HelpButton = () => (
  <a
    href={SUPPORT_WHATSAPP_URL}
    target="_blank"
    rel="noreferrer"
    aria-label="Falar com o atendimento"
    className={iconButtonClass}>
    <CircleHelp className="size-[18px]" />
  </a>
)

export const TrailShell = ({
  open,
  onClose,
  stepIndex,
  stageStepIndex,
  onBack,
  children,
  footer,
  hideProgress,
  showHelp,
  surface,
  hideHeader,
  wide,
  confirmClose = true,
  scrollKey,
  progressOverride,
  headerTitle,
  actionsInBody,
  completed
}: TrailShellProps) => {
  const history = useHistory()
  const scrollRef = useRef<HTMLDivElement>(null)
  // A logo também tira a pessoa da trilha, então passa pela mesma confirmação do "Sair".
  const [pendingExit, setPendingExit] = useState<'close' | 'home' | null>(null)
  const leave = (target: 'close' | 'home') => (target === 'home' ? history.push('/') : onClose())
  const requestExit = (target: 'close' | 'home') => (confirmClose ? setPendingExit(target) : leave(target))
  const requestClose = () => requestExit('close')

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 })
  }, [scrollKey])

  const progress =
    progressOverride ?? Math.max(8, Math.round((Math.min(stepIndex, TRAIL_STEP_TOTAL) / TRAIL_STEP_TOTAL) * 100))
  const closeButton = (
    <button
      type="button"
      onClick={requestClose}
      aria-label="Fechar"
      className={cn(iconButtonClass, 'size-9 border-0 bg-transparent')}>
      <X className="size-5" />
    </button>
  )
  const desktopCloseButton = (
    <button
      type="button"
      onClick={requestClose}
      className="inline-flex h-10 shrink-0 items-center gap-2 rounded-xl border border-solid border-[#E4E4E4] bg-white px-4 text-sm font-medium text-[#1A1A1A] hover:bg-[#F7F7F7]">
      <X className="size-4" />
      Sair
    </button>
  )
  // No desktop o voltar fica no rodapé, ao lado do CTA; sem cabeçalho (ex.: carrinho) a tela tem o próprio voltar.
  const desktopBack = onBack && !hideHeader
  const backButton = onBack ? (
    <button type="button" onClick={onBack} aria-label="Voltar" className={iconButtonClass}>
      <ChevronLeft className="size-5" />
    </button>
  ) : null

  return (
    <Dialog.Root open={open} onOpenChange={(value) => !value && requestClose()}>
      <Dialog.Content
        hideClose
        // Clique fora não fecha: evita perder a triagem ou o pagamento sem querer. O fechamento fica no voltar.
        onInteractOutside={(event) => event.preventDefault()}
        overlayClassName="z-[1000001] bg-white"
        // No desktop vira um wizard de tela cheia; no mobile segue ocupando a tela como antes.
        className="left-0 top-0 z-[1000001] flex h-dvh max-h-none w-full max-w-none translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden rounded-none border-0 bg-white p-0 font-['Inter',sans-serif] shadow-none md:data-[state=open]:zoom-in-100 md:data-[state=open]:slide-in-from-left-0 md:data-[state=open]:slide-in-from-top-0 [&_*]:font-['Inter',sans-serif] [&_input]:text-base [&_select]:text-base [&_textarea]:text-base">
        <Dialog.Title className="sr-only">Triagem do programa de perda de peso</Dialog.Title>
        <header className="hidden shrink-0 flex-col border-0 border-b border-solid border-[#EEEEEE] bg-white md:flex">
          <div className="mx-auto flex h-16 w-full max-w-[1280px] items-center gap-4 px-8">
            <button
              type="button"
              onClick={() => requestExit('home')}
              aria-label="Ir para a página inicial da Cannect"
              className="shrink-0 cursor-pointer border-0 bg-transparent p-0">
              <img src={logo} alt="Cannect" className="block h-[18px] w-[108px] object-contain" />
            </button>
            <div className="ml-auto flex shrink-0 items-center gap-2">
              {showHelp ? <HelpButton /> : null}
              {desktopCloseButton}
            </div>
          </div>
          {!hideProgress ? (
            <div className="mx-auto w-full max-w-[1280px] px-8 pb-4">
              <WizardStepper
                stepIndex={stageStepIndex ?? stepIndex}
                progress={progressOverride}
                completed={completed}
              />
            </div>
          ) : null}
        </header>
        {hideHeader ? <div className="absolute right-3 top-3 z-10 md:hidden">{closeButton}</div> : null}
        {hideHeader ? null : (
          <header className="shrink-0 bg-white px-4 pb-3 pt-4 md:hidden">
            {headerTitle ? (
              <>
                <div className="relative flex items-center justify-center">
                  <div className="absolute left-0">{backButton}</div>
                  <div className="absolute right-0">{closeButton}</div>
                  <div className="text-center">
                    <p className="m-0 text-base font-semibold text-[#1A1A1A]">{headerTitle.title}</p>
                    <p className="m-0 text-xs text-[#6B6560]">{headerTitle.subtitle}</p>
                  </div>
                </div>
                <div className="mt-3 flex">
                  <ProgressBar progress={progress} />
                </div>
              </>
            ) : (
              <div className="flex items-center gap-3">
                {backButton}
                {!hideProgress ? <ProgressBar progress={progress} /> : <div className="flex-1" />}
                {showHelp ? <HelpButton /> : null}
                {closeButton}
              </div>
            )}
          </header>
        )}

        <div
          ref={scrollRef}
          className={cn(
            'flex min-h-0 flex-1 flex-col overflow-y-auto px-4 pb-6 pt-3 md:px-8 md:pb-12 md:pt-12',
            surface ? 'bg-[#F5F6F8]' : 'bg-white'
          )}>
          <main
            className={cn(
              'mx-auto flex min-h-0 w-full flex-1 flex-col',
              wide ? 'max-w-[1100px] md:max-w-[1200px]' : 'max-w-[480px] md:max-w-[680px]'
            )}>
            {children}
          </main>
        </div>

        {footer ? (
          <div
            className={cn(
              'shrink-0 bg-white px-4 pb-5 pt-3 md:border-0 md:border-t md:border-solid md:border-[#EEEEEE] md:px-8 md:py-5',
              surface && 'border-t border-solid border-[#EEEEEE]',
              actionsInBody && !desktopBack && 'md:hidden'
            )}>
            <div
              className={cn(
                'mx-auto flex w-full max-w-[480px] flex-col md:flex-row md:items-start md:justify-between md:gap-6',
                wide ? 'md:max-w-[1200px]' : 'md:max-w-[680px]'
              )}>
              {desktopBack ? (
                <button
                  type="button"
                  onClick={onBack}
                  className="hidden h-12 shrink-0 items-center gap-2 rounded-full border border-solid border-[#E4E4E4] bg-white px-5 text-sm font-semibold text-[#1A1A1A] hover:bg-[#F7F7F7] md:inline-flex">
                  <ChevronLeft className="size-5" />
                  Voltar
                </button>
              ) : null}
              <div className={cn('flex w-full flex-col md:ml-auto md:w-[340px]', actionsInBody && 'md:hidden')}>
                {footer}
              </div>
            </div>
          </div>
        ) : null}
        {pendingExit ? (
          <div
            role="alertdialog"
            aria-modal="true"
            aria-label="Confirmar saída"
            className="absolute inset-0 z-20 flex items-end justify-center bg-[rgba(26,26,26,0.45)] p-4 backdrop-blur-[3px] duration-200 animate-in fade-in md:items-center">
            <div className="flex w-full max-w-[380px] flex-col items-center gap-5 rounded-[28px] bg-white px-6 pb-6 pt-7 text-center shadow-[0_24px_64px_rgba(26,26,26,0.28)] duration-200 animate-in zoom-in-95 slide-in-from-bottom-4">
              <span className="inline-flex size-14 items-center justify-center rounded-2xl bg-marketplace-100 text-marketplace-900">
                <LogOut className="size-6" />
              </span>
              <div className="flex flex-col gap-2">
                <p className="m-0 text-xl font-bold leading-tight text-[#1A1A1A]">Já vai embora?</p>
                <p className="m-0 text-sm leading-[1.5] text-[#6B6560]">
                  Se sair agora, o que você ainda não salvou neste passo será perdido. Você pode retomar o programa
                  depois.
                </p>
              </div>
              <div className="flex w-full flex-col gap-2">
                <TrailCta onClick={() => setPendingExit(null)}>Continuar no programa</TrailCta>
                <button
                  type="button"
                  onClick={() => {
                    setPendingExit(null)
                    leave(pendingExit)
                  }}
                  className="inline-flex h-12 w-full items-center justify-center rounded-full border border-solid border-[#E4E4E4] bg-white text-sm font-semibold text-[#433F3E]">
                  Sair mesmo assim
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </Dialog.Content>
    </Dialog.Root>
  )
}
